/**
 * contactEdit.ts — the pure decisions behind CONTACT editing (Slice E,
 * §15.11 E-3 / E-4 / E-5), shared by the Contact Detail drawer's edit form and
 * the lead drawer's owner field. The RULES are shared here; the CHROME stays
 * separate — the lead drawer is lead-shaped and is not generalised (E-3).
 *
 *  - SCOPE (E-5): full name, email, phone, title, company, location,
 *    category, prospect segment, notes, source, owner. NOT linkedInUrl (not
 *    tracked — an untracked edit path, E-6), NOT tags (A1), NOT status here
 *    (Slice D's inline status).
 *  - CHANGED FIELDS ONLY (E2.2): the PUT is the diff against the stored
 *    record, so one intended edit yields one history row per changed field.
 *  - SB-6: a stored off-vocabulary source pre-selects for honesty but never
 *    rides the wire unless the user changed it.
 */
import type { UpdateContactRequest } from '../../../../api/types'
import type { SalesContact } from '../../types'

export const CATEGORY_OPTIONS = [
  { value: 'university', label: 'University' },
  { value: 'college', label: 'College' },
  { value: 'welfare-to-work', label: 'Welfare-to-Work' },
] as const

export const SEGMENT_OPTIONS = [
  { value: 'current', label: 'Current Prospect' },
  { value: 'future', label: 'Future Prospect' },
] as const

export interface ContactEditDraft {
  name: string
  email: string
  phone: string
  title: string
  company: string
  location: string
  category: string
  prospectSegment: string
  notes: string
  source: string
  /** Employee ID (E360-2 owner wire); '' = unchanged. */
  assignedEmployeeId: string
}

export function draftFromContact(c: SalesContact): ContactEditDraft {
  return {
    name: c.name,
    email: c.email,
    phone: c.phone,
    title: c.title,
    company: c.company,
    location: c.location ?? '',
    category: c.category ?? '',
    prospectSegment: c.prospectSegment ?? '',
    notes: c.notes ?? '',
    source: c.source ?? '',
    assignedEmployeeId: c.assignedEmployeeId ?? '',
  }
}

/**
 * Changed fields only. Empty draft values mean "unchanged" — the wire's own
 * keep-current semantics (clear-to-null is a later slice) — so an emptied
 * input never sends anything.
 */
export function contactEditDiff(before: SalesContact, draft: ContactEditDraft): UpdateContactRequest {
  const body: UpdateContactRequest = {}
  const put = (k: keyof UpdateContactRequest, v: string, was: string) => {
    const t = v.trim()
    if (t !== '' && t !== was) body[k] = t
  }
  put('fullName', draft.name, before.name)
  put('email', draft.email, before.email)
  put('phone', draft.phone, before.phone)
  put('title', draft.title, before.title)
  put('company', draft.company, before.company)
  put('location', draft.location, before.location ?? '')
  put('category', draft.category, before.category ?? '')
  put('prospectSegment', draft.prospectSegment, before.prospectSegment ?? '')
  put('notes', draft.notes, before.notes ?? '')
  put('source', draft.source, before.source ?? '')
  put('assignedEmployeeId', draft.assignedEmployeeId, before.assignedEmployeeId ?? '')
  return body
}

export function contactEditSavable(before: SalesContact, draft: ContactEditDraft, busy: boolean): boolean {
  if (busy) return false
  if (draft.name.trim() === '') return false
  return Object.keys(contactEditDiff(before, draft)).length > 0
}
